import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, UserPlus, Trash2, Save } from "lucide-react";

export default function ApprovalSettingsPage() {
  const [settings, setSettings] = useState({ has_pin: false, approver_emails: [] });
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const load = () => api.get("/org/approval-settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const savePin = async () => {
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) return toast.error("PIN must be 6 digits.");
    if (pin !== confirmPin) return toast.error("PINs do not match.");
    try {
      await api.put("/org/approval-settings", { pin });
      toast.success("PIN saved.");
      setPin(""); setConfirmPin(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addApprover = async () => {
    if (!newEmail.trim()) return;
    const next = [...new Set([...(settings.approver_emails || []), newEmail.trim().toLowerCase()])];
    try {
      await api.put("/org/approval-settings", { approver_emails: next });
      toast.success("Approver added.");
      setNewEmail(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeApprover = async (email) => {
    const next = (settings.approver_emails || []).filter((e) => e !== email);
    try {
      await api.put("/org/approval-settings", { approver_emails: next });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <WorkspaceLayout subtitle="HR workspace" title="Approval Settings">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-sm border-ec-line" data-testid="pin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-ec-emerald" /><h3 className="font-display font-bold text-lg">6-digit issuance PIN</h3></div>
            <p className="text-xs text-ec-ink-soft mb-4">Used to instantly issue Part-Time / Contract / Internship / Achievement / Training / Conduct / Recommendation certificates without OTP. Keep it shared securely with your authorised issuers only.</p>
            <div className="text-xs mb-3" data-testid="pin-status">
              {settings.has_pin
                ? <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30">PIN configured · last updated {settings.updated_at?.slice(0, 10)}</Badge>
                : <Badge className="rounded-sm bg-amber-100 text-amber-900 border-amber-300">No PIN set yet</Badge>
              }
            </div>
            <Label className="eyebrow">New PIN</Label>
            <Input type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className="rounded-sm tracking-[0.5em] text-center text-lg mt-2" placeholder="••••••" data-testid="pin-input" />
            <Label className="eyebrow mt-3 block">Confirm PIN</Label>
            <Input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className="rounded-sm tracking-[0.5em] text-center text-lg mt-2" placeholder="••••••" data-testid="pin-confirm" />
            <Button onClick={savePin} className="rounded-sm mt-4 bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="pin-save"><Save className="w-4 h-4" /> Save PIN</Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-ec-line" data-testid="approvers-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><UserPlus className="w-4 h-4 text-ec-emerald" /><h3 className="font-display font-bold text-lg">Approvers (Full-Time Experience)</h3></div>
            <p className="text-xs text-ec-ink-soft mb-4">Full-Time Employment Experience certificates require multi-step approval. Add the EthicCred emails of your HR Manager and Org Admin (in order). Maximum 5 approvers.</p>
            <div className="flex gap-2">
              <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} type="email" placeholder="approver@yourorg.com" className="rounded-sm" data-testid="approver-input" />
              <Button onClick={addApprover} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="approver-add">Add</Button>
            </div>
            <ul className="mt-4 divide-y divide-ec-line border border-ec-line rounded-sm" data-testid="approvers-list">
              {(settings.approver_emails || []).length === 0 && <li className="px-4 py-6 text-center text-sm text-ec-ink-soft" data-testid="approvers-empty">No approvers configured.</li>}
              {(settings.approver_emails || []).map((e, idx) => (
                <li key={e} className="px-4 py-2 flex items-center gap-2" data-testid={`approver-row-${e}`}>
                  <Badge className="rounded-sm bg-ec-mute border-ec-line text-ec-ink-soft text-[10px]">Step {idx + 1}</Badge>
                  <span className="flex-1 text-sm font-mono-plex">{e}</span>
                  <button onClick={() => removeApprover(e)} className="p-1.5 rounded-sm border border-ec-error/30 text-ec-error hover:bg-ec-error/10" data-testid={`approver-remove-${e}`}><Trash2 className="w-3 h-3" /></button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </WorkspaceLayout>
  );
}
